// CategoriesStep Component
// Step 3: Program categories setup

import React, { useState } from 'react';
import { 
  FaTags, 
  FaPlus, 
  FaEdit, 
  FaTrash, 
  FaCheck,
  FaTimes,
  FaInfoCircle
} from 'react-icons/fa';
import styles from './CategoriesStep.module.css';

const CategoriesStep = ({ formData, updateFormData, onNext, onBack, isEditMode = false }) => {
  const [isAdding, setIsAdding] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [newCategory, setNewCategory] = useState({ name: '', description: '' });

  const categories = formData.categories || [];

  const handleToggleCategory = (categoryId) => {
    const updatedCategories = categories.map(cat =>
      cat.id === categoryId ? { ...cat, enabled: !cat.enabled } : cat
    );
    updateFormData({ categories: updatedCategories });
  };

  const handleAddCategory = () => {
    if (!newCategory.name.trim()) return;

    const newCat = {
      id: `custom-${Date.now()}`,
      name: newCategory.name.trim(),
      description: newCategory.description.trim(),
      enabled: true,
      isDefault: false
    };

    updateFormData({ categories: [...categories, newCat] });
    setNewCategory({ name: '', description: '' });
    setIsAdding(false);
  };

  const handleEditCategory = (categoryId) => {
    const category = categories.find(c => c.id === categoryId);
    if (category) {
      setNewCategory({ name: category.name, description: category.description });
      setEditingId(categoryId);
    }
  };

  const handleSaveEdit = () => {
    if (!newCategory.name.trim()) return;

    const updatedCategories = categories.map(cat =>
      cat.id === editingId
        ? { ...cat, name: newCategory.name.trim(), description: newCategory.description.trim() }
        : cat
    );
    updateFormData({ categories: updatedCategories });
    setNewCategory({ name: '', description: '' });
    setEditingId(null);
  };

  const handleDeleteCategory = (categoryId) => {
    const category = categories.find(c => c.id === categoryId);
    if (category?.isDefault) return; // Can't delete default categories

    const updatedCategories = categories.filter(cat => cat.id !== categoryId);
    updateFormData({ categories: updatedCategories });
  };

  const handleCancelEdit = () => {
    setNewCategory({ name: '', description: '' });
    setEditingId(null);
    setIsAdding(false);
  };

  const enabledCount = categories.filter(c => c.enabled).length;
  const isFormValid = enabledCount > 0;

  return (
    <div className={styles.container}>
      {/* Header */}
      <div className={styles.header}>
        <h2 className={styles.title}>Program Categories</h2>
        <p className={styles.subtitle}>
          Categories help organize your club's programs
        </p>
      </div>

      {/* Content */}
      <div className={styles.content}>
        {/* Info Banner */}
        <div className={styles.infoBanner}>
          <FaInfoCircle className={styles.infoIcon} />
          <div>
            <p className={styles.infoText}>
              {isEditMode 
                ? 'Manage your club\'s program categories. Add new ones or remove existing categories.'
                : 'Default categories are pre-selected. Toggle them on/off or add custom categories for your club.'}
            </p>
            <p className={styles.infoSubtext}>
              {enabledCount} {enabledCount === 1 ? 'category' : 'categories'} {isEditMode ? 'active' : 'will be created'}
            </p>
          </div>
        </div>

        {/* Categories List */}
        <div className={styles.categoriesList}>
          {categories.map(category => (
            <div 
              key={category.id} 
              className={`${styles.categoryItem} ${category.enabled ? styles.enabled : styles.disabled}`}
            >
              {editingId === category.id ? (
                <div className={styles.editForm}>
                  <input
                    type="text"
                    className={styles.input}
                    value={newCategory.name}
                    onChange={(e) => setNewCategory({ ...newCategory, name: e.target.value })}
                    placeholder="Category name"
                    autoFocus
                  />
                  <input
                    type="text"
                    className={styles.input}
                    value={newCategory.description}
                    onChange={(e) => setNewCategory({ ...newCategory, description: e.target.value })}
                    placeholder="Description (optional)"
                  />
                  <div className={styles.editActions}>
                    <button 
                      className={styles.saveButton}
                      onClick={handleSaveEdit}
                      disabled={!newCategory.name.trim()}
                    >
                      <FaCheck /> Save
                    </button>
                    <button 
                      className={styles.cancelEditButton}
                      onClick={handleCancelEdit}
                    >
                      <FaTimes /> Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <label className={styles.categoryToggle}>
                    <input
                      type="checkbox"
                      checked={category.enabled}
                      onChange={() => handleToggleCategory(category.id)}
                    />
                    <span className={styles.checkbox}>
                      {category.enabled && <FaCheck />}
                    </span>
                    <div className={styles.categoryInfo}>
                      <span className={styles.categoryName}>
                        {category.name}
                        {category.isDefault && (
                          <span className={styles.defaultBadge}>Default</span>
                        )}
                      </span>
                      {category.description && (
                        <span className={styles.categoryDescription}>
                          {category.description}
                        </span>
                      )}
                    </div>
                  </label>

                  <div className={styles.categoryActions}>
                    <button
                      className={styles.actionButton}
                      onClick={() => handleEditCategory(category.id)}
                      title="Edit"
                    >
                      <FaEdit />
                    </button>
                    {!category.isDefault && (
                      <button
                        className={`${styles.actionButton} ${styles.deleteAction}`}
                        onClick={() => handleDeleteCategory(category.id)}
                        title="Delete"
                      >
                        <FaTrash />
                      </button>
                    )}
                  </div>
                </>
              )}
            </div>
          ))}
        </div>

        {/* Add Category Form */}
        {isAdding ? (
          <div className={styles.addForm}>
            <h4 className={styles.addFormTitle}>Add Custom Category</h4>
            <input
              type="text"
              className={styles.input}
              value={newCategory.name}
              onChange={(e) => setNewCategory({ ...newCategory, name: e.target.value })}
              placeholder="Category name *"
              autoFocus
            />
            <input
              type="text"
              className={styles.input}
              value={newCategory.description}
              onChange={(e) => setNewCategory({ ...newCategory, description: e.target.value })}
              placeholder="Description (optional)"
            />
            <div className={styles.addFormActions}>
              <button
                className={styles.addCategoryButton}
                onClick={handleAddCategory}
                disabled={!newCategory.name.trim()}
              >
                <FaPlus /> Add Category
              </button>
              <button
                className={styles.cancelAddButton}
                onClick={handleCancelEdit}
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <button
            className={styles.addNewButton}
            onClick={() => setIsAdding(true)}
          >
            <FaPlus /> Add Custom Category
          </button>
        )}
      </div>

      {/* Footer */}
      <div className={styles.footer}>
        <button className={styles.backButton} onClick={onBack}>
          Back
        </button>
        <button
          className={styles.nextButton}
          onClick={onNext}
          disabled={!isFormValid}
        >
          Next
        </button>
      </div>
    </div>
  );
};

export default CategoriesStep;

